import { useEffect, useRef } from 'react';
import { useAppStore } from '@flowfocus/core';
import { notify, notificationPermission } from '../lib/notify';

/**
 * Best-effort task reminders while the app is open.
 *
 * A task's `reminder` field is an absolute timestamp (a time set on its due
 * day). For every pending task whose reminder lands within the next 24h we arm
 * a `setTimeout`; a periodic re-scan pulls in reminders as they enter that
 * window. Fired reminders are remembered in localStorage (keyed by task+time)
 * so a reload doesn't re-notify, and changing the time re-arms it.
 *
 * NOTE: this only fires while a tab is open. Reliable reminders when the app is
 * closed need push (service worker + backend), which will come via Hermes /
 * Telegram — see [[ecosystem-vision]].
 */

const FIRED_KEY = 'ff_fired_reminders';
const WINDOW_MS = 24 * 60 * 60 * 1000; // only schedule reminders due within 24h
const RESCAN_MS = 10 * 60 * 1000;      // re-scan for newly in-window reminders
const GRACE_MS = 60 * 60 * 1000;       // fire a just-missed reminder up to 1h late

function loadFired(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(FIRED_KEY) || '[]')); } catch { return new Set(); }
}
function saveFired(s: Set<string>) {
  try { localStorage.setItem(FIRED_KEY, JSON.stringify([...s].slice(-200))); } catch { /* ignore */ }
}

export function useTaskReminders() {
  const tasks = useAppStore((s) => s.tasks);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const fired = useRef<Set<string>>(loadFired());

  useEffect(() => {
    const scan = () => {
      if (notificationPermission() !== 'granted') return;
      const now = Date.now();

      for (const t of tasks) {
        if (t.completed || !t.reminder) continue;
        const key = `${t.id}:${t.reminder}`;
        if (fired.current.has(key) || timers.current.has(key)) continue;

        const delay = t.reminder - now;
        if (delay <= 0) {
          // Missed while the app was closed — fire once if still recent.
          if (delay > -GRACE_MS) notify('⏰ Recordatorio', t.name);
          fired.current.add(key);
          saveFired(fired.current);
          continue;
        }
        if (delay > WINDOW_MS) continue; // too far off; a later scan will catch it

        const id = setTimeout(() => {
          notify('⏰ Recordatorio', t.name);
          fired.current.add(key);
          saveFired(fired.current);
          timers.current.delete(key);
        }, delay);
        timers.current.set(key, id);
      }

      // Drop timers whose task was completed, deleted, or had its time changed.
      for (const [key, id] of timers.current) {
        const taskId = key.slice(0, key.lastIndexOf(':'));
        const rem = key.slice(key.lastIndexOf(':') + 1);
        const t = tasks.find((x) => x.id === taskId);
        if (!t || t.completed || String(t.reminder) !== rem) {
          clearTimeout(id);
          timers.current.delete(key);
        }
      }
    };

    scan();
    const interval = setInterval(scan, RESCAN_MS);
    return () => clearInterval(interval);
  }, [tasks]);

  // Clear everything on unmount.
  useEffect(() => {
    const map = timers.current;
    return () => { for (const id of map.values()) clearTimeout(id); map.clear(); };
  }, []);
}
